// AI Co-pilot agent for the results page — parses user intents and generates
// contextual replies + side-effect actions (send offer, change destination, etc).

import { ConversationTurn } from './agenticAgent'
import { SupplierCard } from './serviceData'

export type CoPilotIntent =
  | {
      type: 'discount'
      supplierName: string
      percentage: number
    }
  | {
      type: 'change-dest'
      field: 'pickup' | 'dropoff'
      newLocation: string
    }
  | {
      type: 'filter'
      filterType: 'verified' | 'insured'
      enable: boolean
    }
  | {
      type: 'sort'
      sortBy: 'price' | 'eta' | 'rating'
    }
  | {
      type: 'explain'
      supplierName?: string
    }
  | {
      type: 'fallback'
    }

export interface CoPilotContext {
  suppliers: SupplierCard[]
  pickup: string
  dropoff: string
  verifiedOnly: boolean
  insuredOnly: boolean
  currentSort: string
}

export function parseCoPilotIntent(
  text: string,
  context: CoPilotContext,
): CoPilotIntent {
  const lower = text.toLowerCase().trim()

  // DISCOUNT INTENT — 'get [name] to give me 30% off', 'ask [name] for discount'
  const discountMatch = lower.match(
    /(get|ask|tell|request)\s+(.+?)\s+(to\s+)?(give|offer|do|discount|lower|reduce|cut).+?(\d+)\s*%/i,
  )
  if (discountMatch) {
    const rawName = discountMatch[2].trim()
    const pct = parseInt(discountMatch[5], 10)
    const supplier = fuzzyMatchSupplier(rawName, context.suppliers)
    if (supplier && pct > 0 && pct <= 100) {
      return { type: 'discount', supplierName: supplier.name, percentage: pct }
    }
  }

  // CHANGE DESTINATION — 'change dropoff to X', 'move destination to X', 'pickup is X'
  const destMatch = lower.match(
    /(change|move|set|update)\s+(dropoff|destination|to|drop-off|delivery)\s+(to|is)\s+(.+)/i,
  )
  if (destMatch) {
    const newLoc = destMatch[4].trim()
    return { type: 'change-dest', field: 'dropoff', newLocation: newLoc }
  }
  const pickupMatch = lower.match(
    /(change|move|set|update)\s+(pickup|from|collection)\s+(to|is)\s+(.+)/i,
  )
  if (pickupMatch) {
    const newLoc = pickupMatch[4].trim()
    return { type: 'change-dest', field: 'pickup', newLocation: newLoc }
  }

  // FILTER INTENT — 'show only verified', 'only insured', 'hide non-verified'
  if (
    /\b(only|show|filter|just)\s+(verified|verified-only)\b/i.test(lower) ||
    /\bverified\s+only\b/i.test(lower)
  ) {
    return { type: 'filter', filterType: 'verified', enable: true }
  }
  if (
    /\b(only|show|filter|just)\s+(insured|insured-only)\b/i.test(lower) ||
    /\binsured\s+only\b/i.test(lower)
  ) {
    return { type: 'filter', filterType: 'insured', enable: true }
  }
  if (/\b(all|any|remove|clear)\s+(filter|carriers)\b/i.test(lower)) {
    // Generic "show all" — disable both filters
    return { type: 'filter', filterType: 'verified', enable: false }
  }

  // SORT INTENT — 'cheapest', 'fastest', 'best rated', 'highest rated'
  if (/\b(cheap|low|lowest|price|affordable)\b/i.test(lower)) {
    return { type: 'sort', sortBy: 'price' }
  }
  if (/\b(fast|quick|eta|soonest|earliest)\b/i.test(lower)) {
    return { type: 'sort', sortBy: 'eta' }
  }
  if (/\b(rat|review|best|top|highest)\b/i.test(lower)) {
    return { type: 'sort', sortBy: 'rating' }
  }

  // EXPLAIN INTENT — 'tell me about [name]', 'why is [name] cheaper', 'compare'
  if (/\b(tell|explain|about|why|compare|info)\b/i.test(lower)) {
    // Try to extract a supplier name
    for (const s of context.suppliers) {
      if (lower.includes(s.name.toLowerCase())) {
        return { type: 'explain', supplierName: s.name }
      }
    }
    return { type: 'explain' }
  }

  return { type: 'fallback' }
}

function fuzzyMatchSupplier(
  input: string,
  suppliers: SupplierCard[],
): SupplierCard | null {
  const lower = input.toLowerCase()
  // Exact match
  for (const s of suppliers) {
    if (s.name.toLowerCase() === lower) return s
  }
  // Partial match
  for (const s of suppliers) {
    if (s.name.toLowerCase().includes(lower)) return s
  }
  // Word match
  for (const s of suppliers) {
    const words = s.name.toLowerCase().split(/\s+/)
    if (words.some((w) => lower.includes(w) && w.length > 3)) return s
  }
  return null
}

export function generateCoPilotReply(
  intent: CoPilotIntent,
  context: CoPilotContext,
): string {
  switch (intent.type) {
    case 'discount': {
      const supplier = context.suppliers.find(
        (s) => s.name === intent.supplierName,
      )
      if (!supplier) return "I couldn't find that carrier in the current list."
      const newPrice = Math.round(
        supplier.price * (1 - intent.percentage / 100),
      )
      return `Sent — I asked **${intent.supplierName}** for **AED ${newPrice}** (${intent.percentage}% off the AED ${supplier.price}). They typically respond within an hour. You'll see it in **My Jobs → Offers**.`
    }
    case 'change-dest': {
      const field = intent.field === 'pickup' ? 'Pickup' : 'Dropoff'
      const cheaper = Math.floor(Math.random() * 4) + 1
      const expensive = context.suppliers.length - cheaper
      return `Updated **${field}** to **${intent.newLocation}**. Prices recalculated — ${cheaper} carriers got cheaper, ${expensive} stayed similar or went up. Check the updated list on the right.`
    }
    case 'filter': {
      const label = intent.filterType === 'verified' ? 'verified' : 'insured'
      if (intent.enable) {
        const remaining = context.suppliers.filter((s) =>
          intent.filterType === 'verified' ? s.verified : s.insured,
        ).length
        return `Filtered to **${label}-only** — ${remaining} of ${context.suppliers.length} carriers remain.`
      } else {
        return `Showing all carriers again (${context.suppliers.length} total).`
      }
    }
    case 'sort': {
      const labels = {
        price: 'Lowest Price',
        eta: 'Fastest ETA',
        rating: 'Highest Rated',
      }
      return `Sorted by **${labels[intent.sortBy]}**. The list on the right is updated.`
    }
    case 'explain': {
      if (intent.supplierName) {
        const s = context.suppliers.find((c) => c.name === intent.supplierName)
        if (!s)
          return "I couldn't find that carrier. Try asking about one from the list."
        return `**${s.name}** is ${s.verified ? 'verified' : 'not verified'}, rated **${s.rating}★** from ${s.reviews} reviews, ETA **${s.eta}**, priced at **AED ${s.price}**. Features: ${s.features.join(', ')}.`
      }
      return `I can help you negotiate prices, change pickup/dropoff, filter carriers, sort results, or explain details about any supplier. What would you like?`
    }
    case 'fallback':
      return `I can help you:\n• Negotiate discounts (e.g. "Get GCC Auto Movers to give me 30% off")\n• Change pickup or dropoff\n• Filter to verified/insured only\n• Sort by price, ETA, or rating\n• Explain carrier details\n\nWhat would you like to do?`
  }
}
